import { Eyebrow } from "@/components/Eyebrow";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

type Shared = {
  title: ReactNode;
  eyebrow?: string;
  body?: ReactNode;
  mobileAlign?: "start" | "center";
  className?: string;
};
type CenteredProps = Shared & { icon?: ReactNode };
type SplitProps = Shared & { aside?: ReactNode; align?: "end" | "center" };

export function SectionHeader(
  props:
    | ({ variant: "centered" } & CenteredProps)
    | ({ variant: "split" } & SplitProps),
) {
  return props.variant === "centered" ? (
    <CenteredHeader {...props} />
  ) : (
    <SplitHeader {...props} />
  );
}

function CenteredHeader({
  icon,
  eyebrow,
  title,
  body,
  mobileAlign = "center",
  className,
}: CenteredProps) {
  return (
    <div
      className={cn(
        "mx-auto flex max-w-2xl flex-col gap-6",
        mobileAlign === "center"
          ? "items-center text-center"
          : "md:items-center md:text-center",
        className,
      )}
    >
      {icon}
      <Heading eyebrow={eyebrow} title={title} />
      {body && <Body>{body}</Body>}
    </div>
  );
}

function SplitHeader({
  eyebrow,
  title,
  body,
  aside,
  align = "end",
  mobileAlign = "start",
  className,
}: SplitProps) {
  return (
    <div
      className={cn(
        "flex flex-col md:grid md:grid-cols-2 md:gap-10 lg:gap-16",
        aside ? "gap-12" : "gap-6",
        align === "end" ? "md:items-end" : "md:items-center",
        mobileAlign === "center" && "items-center text-center md:text-left",
        className,
      )}
    >
      <div className="flex flex-col gap-6">
        <Heading eyebrow={eyebrow} title={title} />
        {aside && body && <Body>{body}</Body>}
      </div>
      {aside ? (
        <div className="md:justify-self-end">{aside}</div>
      ) : (
        body && <Body className="max-w-lg md:justify-self-end">{body}</Body>
      )}
    </div>
  );
}

function Heading({ eyebrow, title }: Pick<Shared, "eyebrow" | "title">) {
  return (
    <>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <SectionTitle>{title}</SectionTitle>
    </>
  );
}

export function SectionTitle({
  size = "default",
  children,
}: {
  size?: "default" | "small";
  children: ReactNode;
}) {
  return (
    <h2
      className={cn(
        "text-balance leading-tight text-primary md:leading-snug",
        size === "default" ? "text-3xl lg:text-4xl" : "text-2xl lg:text-3xl",
      )}
    >
      {children}
    </h2>
  );
}

function Body({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-6 text-sm leading-normal tracking-body text-secondary md:text-base md:leading-relaxed",
        className,
      )}
    >
      {typeof children === "string" ? <p>{children}</p> : children}
    </div>
  );
}
